import { type Entity } from '@api/api/entity-schema';
import { type SnakeMeta } from '@api/api/timeseries-schema';
import { mapEntity } from '@api/mappers/mapEntity';
import type { TimeSeriesItem } from '@chart';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { PriceDisplayView } from '@terminal/components/price-display-view/PriceDisplayView';
import {
  useIsAwaitingLivePrice,
  useLivePrice,
} from '@terminal/hooks/use-live-price';
import { mapEntityToPriceEntity } from '@terminal/hooks/use-live-price/use-live-price-store/useLivePriceStore.utils';
import { usePreliminaryPrice } from '@terminal/react-query-hooks/prices/usePreliminaryPrice';
import { getEntityPriceVenue } from '@terminal/services/price/price-service';
import { useLegendPriceHover } from '@terminal/views/turbo-chart/hooks/use-legend-price-hover/useLegendPriceHover';
import { getPriceData } from '@terminal/widgets/asset-overview-widget/utils/asset-box/asset-box-helpers';
import clsx from 'clsx';

import styles from './ChartPrice.module.scss';

export interface ChartPriceProps {
  entity: Entity;
  snakeMeta?: SnakeMeta;
  ts?: TimeSeriesItem[];
  className?: string;
  isCondensed?: boolean;
  enablePriceHover?: boolean;
  waitForLiveTick?: boolean;
}

export const ChartPrice = ({
  entity,
  snakeMeta,
  ts = [],
  className,
  isCondensed = false,
  enablePriceHover = true,
  /**
   * When true, show a skeleton until the first websocket tick arrives to prevent a
   * "flash of last close" on surfaces like the earnings insight drawer header
   */
  waitForLiveTick = false,
}: ChartPriceProps) => {
  const mappedEntity = mapEntity(entity);
  const priceHover = useLegendPriceHover({
    elementRef: {
      current: document,
    },
    enabled: enablePriceHover,
  });

  const priceEntities = mapEntityToPriceEntity(entity, snakeMeta);

  const livePriceData = useLivePrice(priceEntities);
  const hoverData = priceHover?.panes[0].data[0];
  const isChartHovered = !!hoverData?.currentPrice;

  const priceData = getPriceData({
    hoverData,
    livePriceData,
    entity: mappedEntity,
    ts,
    priceEntity: priceEntities[0],
  });

  const { data: preliminaryPriceResult } = usePreliminaryPrice(
    entity.subscribable_ticker ?? '',
    getEntityPriceVenue(entity)
  );

  const isAwaitingLiveTick = useIsAwaitingLivePrice({
    hasLiveTick: !!priceData?.isLive,
    enabled: waitForLiveTick && !isChartHovered,
  });

  const wrapperClassName = clsx(
    styles.priceWrapper,
    { [styles.condensed]: isCondensed },
    className
  );

  if (isAwaitingLiveTick) {
    return (
      <div className={wrapperClassName} data-testid="chart-price-loading">
        <SkeletonLoader
          areas={[{ className: styles.priceSkeleton }]}
          withWrapper={false}
        />
      </div>
    );
  }

  return (
    <div className={wrapperClassName}>
      {priceData && (
        <PriceDisplayView
          entity={mappedEntity}
          livePriceInfo={priceData}
          snakeMeta={snakeMeta}
          preliminaryPrice={preliminaryPriceResult?.data}
          isChartHovered={isChartHovered}
          isCondensed={isCondensed}
        />
      )}
    </div>
  );
};
